import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, useReveal } from "./_shared";

export default function JournalSection({ posts }) {
  const ref = useReveal(0.1);
  if (!posts?.length) return null;
  return (
    <section
      className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
      data-testid="journal-section"
    >
      <Reveal className="flex items-end justify-between mb-12">
        <div>
          <p className="eyebrow">The Asvasa Journal</p>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3">
            Style notes & studio stories
          </h2>
        </div>
        <Link
          to="/blog"
          className="hidden md:inline-flex items-center gap-2 eyebrow text-[#1A1A1A] hover:gap-3 transition-all"
          data-testid="see-all-journal"
        >
          All stories <ArrowRight size={14} />
        </Link>
      </Reveal>

      {posts[0] && (
        <Reveal className="mb-16 md:mb-20">
          <Link
            to={`/blog/${posts[0].slug}`}
            className="group grid md:grid-cols-12 gap-8 md:gap-12 items-center"
            data-testid={`journal-featured-${posts[0].slug}`}
          >
            <div className="md:col-span-7 relative overflow-hidden aspect-[5/4] md:aspect-[4/3]">
              <img
                src={posts[0].cover}
                alt={posts[0].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1A1A1A]/30 via-transparent to-transparent" />
              <span className="absolute top-6 left-6 bg-[#1A1A1A] text-[#c3ba9e] text-[10px] tracking-[0.32em] uppercase px-4 py-2">
                Featured · {posts[0].category}
              </span>
            </div>
            <div className="md:col-span-5">
              <p className="eyebrow text-[#1A1A1A]/60">
                {new Date(posts[0].published_at).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}{" "}
                · {posts[0].read_minutes} min read
              </p>
              <h3 className="font-serif-display text-3xl md:text-5xl mt-4 leading-[1.05] group-hover:translate-x-1 transition-transform duration-500">
                {posts[0].title}
              </h3>
              <p className="text-[15px] text-[#1A1A1A]/75 mt-6 leading-[1.8]">{posts[0].excerpt}</p>
              <span className="inline-flex items-center gap-2 mt-8 text-[11px] tracking-[0.28em] uppercase text-[#1A1A1A] border-b border-[#1A1A1A]/40 pb-1 group-hover:border-[#1A1A1A] group-hover:gap-3 transition-all">
                Read the story <ArrowRight size={14} />
              </span>
            </div>
          </Link>
        </Reveal>
      )}

      <div
        ref={ref}
        className="grid md:grid-cols-3 gap-8 md:gap-10 pt-12 border-t border-[#1A1A1A]/15 reveal-stagger"
      >
        {posts.slice(1).map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            className="group block lift-card"
            data-testid={`journal-card-${p.slug}`}
          >
            <div className="aspect-[4/5] overflow-hidden bg-[#b8af92]">
              <img
                src={p.cover}
                alt={p.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms] ease-out"
                loading="lazy"
              />
            </div>
            <p className="eyebrow mt-5">{p.category}</p>
            <h3 className="font-serif-display text-2xl mt-2 leading-tight group-hover:translate-x-1 transition-transform duration-500">
              {p.title}
            </h3>
            <p className="text-sm text-[#1A1A1A]/70 mt-2 line-clamp-2">{p.excerpt}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
